import FAQs from '@/pages/FAQs';
import Vibes from '@/pages/Vibes';
import BusinessTimezoneSync from '@/components/BusinessTimezoneSync';
import BusinessContinue from '@/pages/business/BusinessContinue';
import BusinessOnboarding from '@/pages/business/BusinessOnboarding';
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/site/Layout";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import AiExplained from "@/pages/AiExplained";
import PeopleMode from "@/pages/PeopleMode";
import Download from "@/pages/Download";
import Pricing from "@/pages/Pricing";
import Support from "@/pages/Support";
import Business from "@/pages/Business";
import Events from "@/pages/Events";
import GetStarted from "@/pages/GetStarted";
import About from "@/pages/About";
import LoginChoice from '@/pages/LoginChoice';
import BusinessLogin from '@/pages/business/BusinessLogin';
import BusinessRegister from '@/pages/business/BusinessRegister';
import BusinessWorkspace from '@/pages/business/BusinessWorkspace';
import BusinessRecovery from '@/pages/business/BusinessRecovery';
import AppDestination from "@/components/site/AppDestination";
import Benefits from "@/pages/Benefits";
import DeleteAccount from "@/pages/DeleteAccount";
import DeleteVerify from "@/pages/DeleteVerify";
import PoliciesIndex from "@/pages/PoliciesIndex";
import PolicyPage from "@/pages/PolicyPage";
import Report from "@/pages/Report";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const POLICY_ROUTES = [
  "/privacy", "/terms", "/community-guidelines", "/safety", "/location-privacy",
  "/child-safety", "/moderation-appeals", "/professional-services",
  "/professional-verification", "/copyright", "/cookies", "/refunds",
  "/photo-verification", "/ai-transparency", "/professional-terms",
  "/business-terms", "/event-meetup-terms", "/age-eligibility",
  "/deletion-policy", "/support-policy",
];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <BusinessTimezoneSync />
        <Routes>
          {/* Control Centre (react-native-web port) — no marketing chrome */}
          <Route path="/control-centre/*" element={<AppDestination destination="/control" label="Control Centre" />} />
          <Route
            path="/*"
            element={
              <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/how-orrbitt-uses-ai" element={<AiExplained />} />
                  <Route path="/faqs" element={<FAQs />} />
                  <Route path="/vibes" element={<Vibes />} />
                  <Route path="/people" element={<PeopleMode />} />
                  {/* Professional Mode is temporarily disabled site-wide; page/code preserved, not deleted */}
                  <Route path="/professional" element={<Navigate to="/people" replace />} />
                  <Route path="/professionals" element={<Navigate to="/people" replace />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/benefits-of-human-connection" element={<Benefits />} />
                  <Route path="/business" element={<Business />} />
                  <Route path="/business/login" element={<BusinessLogin />} />
                  <Route path="/business/register" element={<BusinessRegister />} />
                  <Route path="/business/forgot-password" element={<BusinessRecovery />} />
                  <Route path="/business/reset-password" element={<BusinessRecovery />} />
                  <Route path="/business/verify" element={<BusinessContinue />} />
                  <Route path="/business/onboarding" element={<BusinessOnboarding />} />
                  <Route path="/business/pending" element={<BusinessWorkspace section="verification" />} />
                  <Route path="/business/meetup-spot" element={<BusinessWorkspace section="meetup-spot" />} />
                  <Route path="/business/map" element={<BusinessWorkspace section="map" />} />
                  <Route path="/business/profile-edit" element={<BusinessWorkspace section="profile-edit" />} />
                  <Route path="/business/hours" element={<BusinessWorkspace section="hours" />} />
                  <Route path="/business/email" element={<BusinessWorkspace section="email" />} />
                  <Route path="/business/privacy" element={<BusinessWorkspace section="privacy" />} />
                  <Route path="/business/date-time" element={<BusinessWorkspace section="date-time" />} />
                  <Route path="/business/language" element={<BusinessWorkspace section="language" />} />
                  <Route path="/business/help" element={<BusinessWorkspace section="help" />} />
                  <Route path="/business/promote" element={<BusinessWorkspace section="promote" />} />
                  <Route path="/business/redeem" element={<BusinessWorkspace section="redeem" />} />
                  <Route path="/business/delete" element={<BusinessWorkspace section="delete" />} />
                  <Route path="/business/dashboard" element={<BusinessWorkspace section="dashboard" />} />
                  <Route path="/business/events" element={<BusinessWorkspace section="events" />} />
                  <Route path="/business/analytics" element={<BusinessWorkspace section="analytics" />} />
                  <Route path="/business/reviews" element={<BusinessWorkspace section="reviews" />} />
                  <Route path="/business/notifications" element={<BusinessWorkspace section="notifications" />} />
                  <Route path="/business/profile" element={<BusinessWorkspace section="profile" />} />
                  <Route path="/business/verification" element={<BusinessWorkspace section="verification" />} />
                  <Route path="/business/subscription" element={<BusinessWorkspace section="subscription" />} />
                  <Route path="/business/offers" element={<BusinessWorkspace section="offers" />} />
                  <Route path="/business/settings" element={<BusinessWorkspace section="settings" />} />
                  <Route path="/app" element={<Navigate to="/download" replace />} />
                  <Route path="/login" element={<LoginChoice />} />
                  <Route path="/register" element={<Navigate to="/download" replace />} />
                  <Route path="/join" element={<Navigate to="/download" replace />} />
                  <Route path="/get-started" element={<GetStarted />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/download" element={<Download />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/policies" element={<PoliciesIndex />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/report" element={<Report />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/delete-account/verify" element={<DeleteVerify />} />
                  {POLICY_ROUTES.map((r) => (
                    <Route key={r} path={r} element={<PolicyPage />} />
                  ))}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;

